import React, { useState } from "react";
import {
  Paper,
  Text,
  Box,
  Button,
  Stack,
  Container,
  TextInput,
  Group,
  Avatar,
  Card,
  Image,
  ActionIcon,
  Divider,
  ThemeIcon,
  Select,
  Title,
  Badge,
} from "@mantine/core";
import {
  IconHeart,
  IconMessageCircle,
  IconShare,
  IconBookmark,
  IconCheck,
  IconX,
  IconLock,
  IconPhoto,
  IconDots,
  IconCalendar,
  IconUser,
  IconShieldCheck,
} from "@tabler/icons-react";
import { useGlobal } from "../../context/global-context";
import { apiFetch, describeApiError } from "../../utils/api";
import { runStatusCheck } from "../../utils/status-check";
import { ConsentCheckbox, ConsentIndicator } from "../../components/consent-checkbox";
import { consentBodyFields, isSiteConsent } from "../../utils/consent";
import VerificationIframe from "../../components/verification-iframe";

// Sample social media posts data
const SAMPLE_POSTS = [
  {
    id: 1,
    user: {
      name: "Sarah Johnson",
      username: "@sarahj",
      avatar: null,
    },
    content: "Just had the most amazing sunset view from my apartment! Nature never fails to amaze me.",
    image: "/Gemini_Generated_Image_1q8ud51q8ud51q8u.png",
    likes: 234,
    comments: 18,
    shares: 5,
    time: "2h ago",
  },
  {
    id: 2,
    user: {
      name: "Mike Chen",
      username: "@mikechen",
      avatar: null,
    },
    content: "Finally finished my morning workout routine! 💪 Who else is crushing their fitness goals this year?",
    image: "/Gemini_Generated_Image_i8x5pui8x5pui8x5.png",
    likes: 89,
    comments: 12,
    shares: 2,
    time: "4h ago",
  },
  {
    id: 3,
    user: {
      name: "Emily Davis",
      username: "@emilyd",
      avatar: null,
    },
    content: "Coffee + good book = perfect Sunday morning ☕📚",
    image: "/Gemini_Generated_Image_vizwehvizwehvizw.png",
    likes: 567,
    comments: 45,
    shares: 12,
    time: "6h ago",
  },
  {
    id: 4,
    user: {
      name: "Alex Rivera",
      username: "@alexr",
      avatar: null,
    },
    content: "Exploring the city streets today. Found this amazing hidden gem of a restaurant!",
    image: null,
    likes: 342,
    comments: 28,
    shares: 8,
    time: "8h ago",
  },
];

// Post component
function Post({ post, blurred }) {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder style={{ filter: blurred ? "blur(8px)" : "none" }}>
      <Group justify="space-between" mb="md">
        <Group>
          <Avatar color="blue" radius="xl">
            {post.user.name.charAt(0)}
          </Avatar>
          <div>
            <Text fw={500} size="sm">{post.user.name}</Text>
            <Text size="xs" c="dimmed">{post.user.username} · {post.time}</Text>
          </div>
        </Group>
        <ActionIcon variant="subtle" color="gray">
          <IconDots size={18} />
        </ActionIcon>
      </Group>

      <Text size="sm" mb="md">{post.content}</Text>

      {post.image && (
        <Card.Section mb="md">
          <Image src={post.image} height={200} alt="Post image" />
        </Card.Section>
      )}

      <Group justify="space-between">
        <Group gap="lg">
          <Group gap={4}>
            <ActionIcon
              variant="subtle"
              color={liked ? "red" : "gray"}
              onClick={() => setLiked(!liked)}
            >
              <IconHeart size={20} fill={liked ? "currentColor" : "none"} />
            </ActionIcon>
            <Text size="sm" c="dimmed">{liked ? post.likes + 1 : post.likes}</Text>
          </Group>
          <Group gap={4}>
            <ActionIcon variant="subtle" color="gray">
              <IconMessageCircle size={20} />
            </ActionIcon>
            <Text size="sm" c="dimmed">{post.comments}</Text>
          </Group>
          <Group gap={4}>
            <ActionIcon variant="subtle" color="gray">
              <IconShare size={20} />
            </ActionIcon>
            <Text size="sm" c="dimmed">{post.shares}</Text>
          </Group>
        </Group>
        <ActionIcon
          variant="subtle"
          color={saved ? "yellow" : "gray"}
          onClick={() => setSaved(!saved)}
        >
          <IconBookmark size={20} fill={saved ? "currentColor" : "none"} />
        </ActionIcon>
      </Group>
    </Card>
  );
}

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" }, { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" }, { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" }, { value: "DC", label: "District of Columbia" },
];

export default function SocialMediaDemo() {
  const { socialMediaFlow, updateSocialMediaFlow, wineStoreSettings } = useGlobal();
  const [consentAcceptedAt, setConsentAcceptedAt] = useState(null);
  const consentRequired = isSiteConsent(wineStoreSettings) && !consentAcceptedAt;

  const optionalFields = wineStoreSettings?.optionalFields || {};

  // Local UI state
  const [ageFailed, setAgeFailed] = useState(false);
  const [failureCount, setFailureCount] = useState(0);
  const [verificationIframeOpened, setVerificationIframeOpened] = useState(false);
  const [verificationLink, setVerificationLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [formError, setFormError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [apiError, setApiError] = useState(null);

  // Use flow state from context
  const flowStep = socialMediaFlow.step;
  const email = socialMediaFlow.email;
  const username = socialMediaFlow.username;

  const setFlowStep = (step) => updateSocialMediaFlow({ step });
  const setEmail = (email) => updateSocialMediaFlow({ email });
  const setUsername = (username) => updateSocialMediaFlow({ username });

  // Form data for optional fields
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    dobMonth: "",
    dobDay: "",
    dobYear: "",
    ssn4: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const formatPhone = (value) => {
    let digits = value.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
    digits = digits.slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleFormChange = (field, value) => {
    const newValue = field === "phone" ? formatPhone(value) : value;
    setFormData((prev) => ({ ...prev, [field]: newValue }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const isEmailValid = email && /\S+@\S+\.\S+/.test(email);
  const isUsernameValid = username && username.length >= 3;

  const isFormComplete =
    isEmailValid &&
    isUsernameValid &&
    (!optionalFields.firstName || formData.firstName) &&
    (!optionalFields.lastName || formData.lastName) &&
    (!optionalFields.phone || formData.phone) &&
    (!optionalFields.dob || (formData.dobMonth && formData.dobDay && formData.dobYear)) &&
    (!optionalFields.ssn4 || (formData.ssn4 && formData.ssn4.length === 4)) &&
    (!optionalFields.address || (formData.address && formData.city && formData.state && formData.zipCode));

  const handleSignupSubmit = async () => {
    setFormError("");
    setFormErrors({});

    if (!isEmailValid) {
      setFormError("Please enter a valid email address");
      return;
    }
    if (!isUsernameValid) {
      setFormError("Username must be at least 3 characters");
      return;
    }

    // Validate optional fields
    const errors = {};
    if (optionalFields.firstName && !formData.firstName) errors.firstName = "First name is required";
    if (optionalFields.lastName && !formData.lastName) errors.lastName = "Last name is required";
    if (optionalFields.phone && !formData.phone) errors.phone = "Phone number is required";
    if (optionalFields.dob && (!formData.dobMonth || !formData.dobDay || !formData.dobYear)) errors.dob = "Date of birth is required";
    if (optionalFields.ssn4 && (!formData.ssn4 || formData.ssn4.length !== 4)) errors.ssn4 = "Last 4 of SSN is required";
    if (optionalFields.address) {
      if (!formData.address) errors.address = "Address is required";
      if (!formData.city) errors.city = "City is required";
      if (!formData.state) errors.state = "State is required";
      if (!formData.zipCode) errors.zipCode = "ZIP code is required";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    await triggerVerification();
  };

  const triggerVerification = async () => {
    if (failureCount >= 2) {
      setAgeFailed(true);
      return;
    }

    setLoading(true);
    setLoadingMessage("Getting verification session...");
    setApiError(null);

    try {
      const data = await apiFetch("/demo/vx/create-journey.php", {
            email: email,
            ...(optionalFields.phone && formData.phone ? { phone: formData.phone.replace(/\D/g, "") } : {}),
            target: {
              ...(optionalFields.firstName && formData.firstName ? { fn: formData.firstName } : {}),
              ...(optionalFields.lastName && formData.lastName ? { ln: formData.lastName } : {}),
              ...(optionalFields.phone && formData.phone ? { phone: formData.phone.replace(/\D/g, "") } : {}),
              ...(optionalFields.dob && formData.dobYear ? { dob: `${formData.dobYear}${formData.dobMonth.padStart(2, "0")}${formData.dobDay.padStart(2, "0")}` } : {}),
              ...(optionalFields.ssn4 && formData.ssn4 ? { ssn4: formData.ssn4 } : {}),
              ...(optionalFields.address && formData.address ? { addr: formData.address, city: formData.city, state: formData.state, zip: formData.zipCode } : {}),
            },
            demo: "social-media",
            age: `${wineStoreSettings?.age ?? 21}+`,
            method: wineStoreSettings?.integrationMethod || "iframe",
            test_mode: wineStoreSettings?.testMode ?? true,
            ...consentBodyFields(wineStoreSettings, consentAcceptedAt),
      });

      if (data.link) {
        if ((wineStoreSettings?.integrationMethod || "iframe") === "redirect") {
          window.open(data.link, "_blank");
          return;
        }

        setVerificationLink(data.link);
        setVerificationIframeOpened(true);
      } else {
        throw new Error("No verification link received");
      }
    } catch (error) {
      console.error("Verification error:", error);
      setApiError(describeApiError(error));
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  // Listen for messages from the iframe
  // Handle redirect return - check for request_id in query params
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestId = params.get("request_id");
    if (!requestId) return;

    const checkResult = async () => {
      setLoading(true);
      setLoadingMessage("Checking verification result...");
      setApiError(null);

      try {
        const data = await apiFetch("/demo/vx/get-result.php", {
          request_id: requestId,
          demo: "social-media",
        });

        if (data.status === "PASS") {
          setFlowStep("verified");
          setAgeFailed(false);
        } else {
          setAgeFailed(true);
          setFailureCount((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Get result error:", error);
        setApiError(describeApiError(error));
      } finally {
        setLoading(false);
        setLoadingMessage("");
        window.history.replaceState({}, "", window.location.pathname);
      }
    };

    checkResult();
  }, []); // Run once on mount

  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "CLOSE") {
        runStatusCheck("social-media");
        setVerificationIframeOpened(false);
      }
      if (event.data === "PASS") {
        runStatusCheck("social-media");
        setVerificationIframeOpened(false);
        setFlowStep("verified");
        setAgeFailed(false);
      }
      if (event.data === "FAIL") {
        runStatusCheck("social-media");
        setVerificationIframeOpened(false);
        setAgeFailed(true);
        setFailureCount((prev) => prev + 1);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const isVerified = flowStep === "verified";

  return (
    <>
      {/* Keep input font-size at 16px+ so mobile browsers don't zoom on focus */}
      <style>{`
        .vt-no-zoom input,
        .vt-no-zoom textarea,
        .vt-no-zoom select {
          font-size: 16px;
        }
      `}</style>
      <Box className="vt-no-zoom" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <Container size="sm" py="xl">
          {/* Landing State - Show hero and CTA */}
          {flowStep === "landing" && (
            <Paper p="xl" radius="md" shadow="md" mb="xl" style={{ textAlign: "center" }}>
              <Stack gap="lg" align="center">
                <ThemeIcon size={80} radius="xl" color="blue" variant="light">
                  <IconMessageCircle size={40} />
                </ThemeIcon>
                <div>
                  <Title order={2} mb="xs">Welcome to SocialConnect</Title>
                  <Text c="dimmed" size="lg">
                    Connect with friends, share moments, and discover what's happening around you.
                  </Text>
                </div>
                <Divider w="100%" />
                <Stack gap="xs" w="100%">
                  <Group justify="center" gap="xl">
                    <Stack gap={4} align="center">
                      <Text fw={700} size="xl">10M+</Text>
                      <Text size="sm" c="dimmed">Active Users</Text>
                    </Stack>
                    <Stack gap={4} align="center">
                      <Text fw={700} size="xl">50M+</Text>
                      <Text size="sm" c="dimmed">Posts Daily</Text>
                    </Stack>
                    <Stack gap={4} align="center">
                      <Text fw={700} size="xl">100+</Text>
                      <Text size="sm" c="dimmed">Countries</Text>
                    </Stack>
                  </Group>
                </Stack>
                <Button size="lg" fullWidth onClick={() => setFlowStep("signup")}>
                  Create Your Account
                </Button>
                <Text size="sm" c="dimmed">
                  Already have an account? <Text component="span" c="blue" style={{ cursor: "pointer" }}>Log in</Text>
                </Text>
              </Stack>
            </Paper>
          )}

          {/* Signup State - Collect email, username, and optional fields */}
          {(flowStep === "signup" || flowStep === "dob") && (
            <Paper p="xl" radius="md" shadow="md" mb="xl">
              <Stack gap="lg">
                <Group>
                  <ThemeIcon size="xl" radius="xl" color="blue">
                    <IconUser size={24} />
                  </ThemeIcon>
                  <div>
                    <Title order={3}>Create Your Account</Title>
                    <Text size="sm" c="dimmed">
                      Join millions of people sharing their stories.
                    </Text>
                  </div>
                </Group>

                <Divider />

                <Stack gap="sm">
                  <TextInput
                    label="Email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isVerified}
                    required
                  />

                  <TextInput
                    label="Username"
                    placeholder="Choose a username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/\s/g, "").toLowerCase())}
                    description="This is how others will see you"
                    disabled={isVerified}
                    required
                  />

                  {(optionalFields.firstName || optionalFields.lastName) && (
                    <Group grow>
                      {optionalFields.firstName && (
                        <TextInput
                          label="First Name"
                          placeholder="John"
                          value={formData.firstName}
                          onChange={(e) => handleFormChange("firstName", e.target.value)}
                          error={formErrors.firstName}
                          disabled={isVerified}
                          required
                        />
                      )}
                      {optionalFields.lastName && (
                        <TextInput
                          label="Last Name"
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={(e) => handleFormChange("lastName", e.target.value)}
                          error={formErrors.lastName}
                          disabled={isVerified}
                          required
                        />
                      )}
                    </Group>
                  )}
                  {optionalFields.phone && (
                    <TextInput
                      label="Phone"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => handleFormChange("phone", e.target.value)}
                      error={formErrors.phone}
                      disabled={isVerified}
                      required
                    />
                  )}
                  {optionalFields.dob && (
                    <Group grow>
                      <Select
                        label="Month"
                        placeholder="Month"
                        data={MONTHS}
                        value={formData.dobMonth}
                        onChange={(value) => handleFormChange("dobMonth", value)}
                        error={formErrors.dob}
                        disabled={isVerified}
                        required
                      />
                      <Select
                        label="Day"
                        placeholder="Day"
                        data={Array.from({ length: 31 }, (_, i) => ({
                          value: String(i + 1),
                          label: String(i + 1),
                        }))}
                        value={formData.dobDay}
                        onChange={(value) => handleFormChange("dobDay", value)}
                        error={formErrors.dob ? " " : ""}
                        disabled={isVerified}
                        required
                      />
                      <Select
                        label="Year"
                        placeholder="Year"
                        data={Array.from({ length: 100 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return { value: String(year), label: String(year) };
                        })}
                        value={formData.dobYear}
                        onChange={(value) => handleFormChange("dobYear", value)}
                        error={formErrors.dob ? " " : ""}
                        disabled={isVerified}
                        required
                        searchable
                      />
                    </Group>
                  )}
                  {optionalFields.ssn4 && (
                    <TextInput
                      label="Last 4 of SSN"
                      placeholder="1234"
                      value={formData.ssn4}
                      onChange={(e) => handleFormChange("ssn4", e.target.value.replace(/\D/g, "").slice(0, 4))}
                      maxLength={4}
                      error={formErrors.ssn4}
                      disabled={isVerified}
                      required
                    />
                  )}
                  {optionalFields.address && (
                    <>
                      <TextInput
                        label="Address"
                        placeholder="123 Main St"
                        value={formData.address}
                        onChange={(e) => handleFormChange("address", e.target.value)}
                        error={formErrors.address}
                        disabled={isVerified}
                        required
                      />
                      <Group grow>
                        <TextInput
                          label="City"
                          placeholder="New York"
                          value={formData.city}
                          onChange={(e) => handleFormChange("city", e.target.value)}
                          error={formErrors.city}
                          disabled={isVerified}
                          required
                        />
                        <Select
                          label="State"
                          placeholder="Select state"
                          data={US_STATES}
                          value={formData.state}
                          onChange={(value) => handleFormChange("state", value)}
                          error={formErrors.state}
                          disabled={isVerified}
                          required
                        />
                        <TextInput
                          label="ZIP Code"
                          placeholder="10001"
                          value={formData.zipCode}
                          onChange={(e) => handleFormChange("zipCode", e.target.value)}
                          error={formErrors.zipCode}
                          disabled={isVerified}
                          required
                        />
                      </Group>
                    </>
                  )}
                </Stack>

                {formError && (
                  <Text size="sm" c="red">{formError}</Text>
                )}

                {ageFailed && (
                  <Paper p="sm" bg="red.0" radius="sm">
                    <Group>
                      <ThemeIcon color="red" variant="light" size="sm">
                        <IconX size={14} />
                      </ThemeIcon>
                      <Text size="sm" c="red">
                        {failureCount >= 2
                          ? "Maximum attempts reached - Please contact support"
                          : "Verification failed - Please try again"}
                      </Text>
                    </Group>
                  </Paper>
                )}

                {apiError && (
                  <Paper p="sm" bg="red.0" radius="sm">
                    <Group>
                      <ThemeIcon color="red" variant="light" size="sm">
                        <IconX size={14} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={600} c="red">{apiError.error}</Text>
                        <Text size="sm" c="red">{apiError.message}</Text>
                      </div>
                    </Group>
                  </Paper>
                )}

                <ConsentCheckbox brand="socialconnect" acceptedAt={consentAcceptedAt} onChange={setConsentAcceptedAt} disabled={isVerified || loading} />

                <Button
                  fullWidth
                  size="lg"
                  onClick={handleSignupSubmit}
                  loading={loading}
                  disabled={!isFormComplete || failureCount >= 2 || consentRequired}
                  loaderProps={{ type: "dots" }}
                >
                  {loading
                    ? loadingMessage
                    : failureCount >= 2
                      ? "Verification Unavailable"
                      : ageFailed
                        ? "Try Again"
                        : "Complete Sign Up"}
                </Button>
                <ConsentIndicator />

                <Text size="xs" c="dimmed" ta="center">
                  By signing up, you agree to our Terms of Service and Privacy Policy.
                </Text>
              </Stack>
            </Paper>
          )}

          {/* Welcome Message (only if verified) */}
          {isVerified && (
            <Paper p="xl" radius="md" shadow="md" mb="xl" style={{ background: "linear-gradient(135deg, #228be6 0%, #15aabf 100%)" }}>
              <Group justify="space-between" align="flex-start">
                <Stack gap="xs">
                  <Group gap="sm">
                    <ThemeIcon size="lg" radius="xl" color="white" variant="white">
                      <IconCheck size={20} color="#228be6" />
                    </ThemeIcon>
                    <Badge color="white" variant="white" size="lg" style={{ color: "#228be6" }}>
                      Age Verified
                    </Badge>
                  </Group>
                  <Title order={2} c="white">Welcome to SocialConnect, {username}!</Title>
                  <Text c="white" opacity={0.9}>
                    Your account has been verified. You now have full access to the platform.
                  </Text>
                </Stack>
                <Avatar color="white" radius="xl" size="xl" style={{ border: "3px solid white" }}>
                  {username ? username.charAt(0).toUpperCase() : "U"}
                </Avatar>
              </Group>
            </Paper>
          )}

          {/* Create Post Box (only if verified) */}
          {isVerified && (
            <Paper p="md" radius="md" shadow="sm" mb="lg">
              <Group>
                <Avatar color="blue" radius="xl">
                  {username ? username.charAt(0).toUpperCase() : "U"}
                </Avatar>
                <TextInput
                  placeholder={`What's on your mind, ${username || "friend"}?`}
                  style={{ flex: 1 }}
                  radius="xl"
                  disabled
                />
                <ActionIcon variant="light" color="blue" size="lg">
                  <IconPhoto size={20} />
                </ActionIcon>
              </Group>
            </Paper>
          )}

          {/* Social Feed */}
          <Stack gap="lg">
            {SAMPLE_POSTS.map((post) => (
              <Post key={post.id} post={post} blurred={!isVerified} />
            ))}
          </Stack>

          {/* Blurred overlay message - only on landing */}
          {flowStep === "landing" && (
            <Paper
              p="xl"
              radius="md"
              shadow="md"
              mt="xl"
              style={{ textAlign: "center" }}
            >
              <IconLock size={48} style={{ color: "#adb5bd", marginBottom: 16 }} />
              <Title order={4} mb="sm">Join to See More</Title>
              <Text c="dimmed" mb="md">
                Create an account to see posts, connect with friends, and join the conversation.
              </Text>
              <Button onClick={() => setFlowStep("signup")}>
                Sign Up Now
              </Button>
            </Paper>
          )}
        </Container>
      </Box>

      <VerificationIframe
        opened={verificationIframeOpened}
        onClose={() => setVerificationIframeOpened(false)}
        verificationLink={verificationLink}
        title="Age Verification"
      />
    </>
  );
}
